// src/pages/SecurityRoles.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Shield,
  Users,
  UserCheck,
  Lock,
  AlertTriangle,
  Plus,
  Edit,
  Trash2,
  X,
  Search,
  Check,
  Settings,
  Save,
  UserPlus,
} from "lucide-react";
import { securityRolesService } from "../services/security-roles";
import {
  Role,
  Permission,
  SecurityPolicy,
  UserRoleAssignment,
  SecurityPolicyForm,
} from "../types/security-roles";
import Input from "../components/common/Input";
import Select from "../components/common/Select";
import Card from "../components/common/Card";

const SecurityRolesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("roles");

  // Data states
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [securityPolicy, setSecurityPolicy] = useState<SecurityPolicy | null>(
    null
  );
  const [assignments, setAssignments] = useState<UserRoleAssignment[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Form states
  const [roleForm, setRoleForm] = useState({
    name: "",
    displayName: "",
    description: "",
    permissions: [] as string[],
    color: "#3B82F6",
    isActive: true,
  });

  const [assignForm, setAssignForm] = useState({
    userId: "",
    roleIds: [] as string[],
    branchIds: [] as string[],
  });

  const [policyForm, setPolicyForm] = useState({
    sessionTimeoutMinutes: 30,
    maxLoginAttempts: 5,
    requirePasswordChange: true,
    passwordChangeIntervalDays: 90,
    allowMultipleSessions: false,
    auditLogRetentionDays: 365,
  });

  const tabs = [
    { id: "roles", label: "Roles & Permissions", icon: Users },
    { id: "assignments", label: "User Assignments", icon: UserCheck },
    { id: "security", label: "Security Policies", icon: Lock },
    { id: "audit", label: "Access Control", icon: Shield },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await securityRolesService.getSecurityRoles();
      const data = response.data || response;

      // Safely set data with fallbacks
      setRoles(Array.isArray(data?.roles) ? data.roles : []);
      setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
      setSecurityPolicy(data?.securityPolicy || null);

      // Set initial policy form values if policy exists
      if (data?.securityPolicy) {
        setPolicyForm({
          sessionTimeoutMinutes:
            data.securityPolicy.sessionPolicy?.maxSessionDuration || 30,
          maxLoginAttempts:
            data.securityPolicy.loginPolicy?.maxFailedAttempts || 5,
          requirePasswordChange:
            data.securityPolicy.passwordPolicy?.maxAge > 0 || true,
          passwordChangeIntervalDays:
            data.securityPolicy.passwordPolicy?.maxAge || 90,
          allowMultipleSessions:
            data.securityPolicy.sessionPolicy?.maxConcurrentSessions > 1 ||
            false,
          auditLogRetentionDays: 365,
        });
      }

      // Load users for assignments
      await loadUsers();
    } catch (error: any) {
      console.error("Error loading security data:", error);

      // Set safe defaults on error
      setRoles([]);
      setPermissions([]);
      setSecurityPolicy(null);
      setUsers([]);

      toast.error(
        "Failed to load security data: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Use the existing users endpoint
      const response = await fetch("/api/users", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const userData = await response.json();
          setUsers(userData.users || userData.data || []);
        } else {
          console.warn("Users endpoint returned non-JSON response");
          setUsers([]);
        }
      } else {
        console.warn("Users endpoint not available, status:", response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error("Error loading users:", error);
      setUsers([]); // Set empty array on error
    }
  };

  const handleCreateRole = async () => {
    if (!roleForm.name || !roleForm.displayName) {
      toast.error("Role name and display name are required");
      return;
    }

    try {
      setLoading(true);
      const roleData = {
        ...roleForm,
        name: roleForm.name.toLowerCase().replace(/\s+/g, "_"),
      };

      const response = await securityRolesService.createRole(roleData);
      setRoles((prev) => [...prev, response.data]);
      setShowRoleModal(false);
      resetRoleForm();
      toast.success("Role created successfully");
      // Reload data to ensure consistency
      await loadData();
    } catch (error: any) {
      toast.error(
        "Failed to create role: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole || !roleForm.name || !roleForm.displayName) {
      toast.error("Role name and display name are required");
      return;
    }

    try {
      setLoading(true);
      const roleData = {
        ...roleForm,
        name: roleForm.name.toLowerCase().replace(/\s+/g, "_"),
      };

      const response = await securityRolesService.updateRole(
        editingRole._id!,
        roleData
      );
      setRoles((prev) =>
        prev.map((role) =>
          role._id === editingRole._id ? response.data : role
        )
      );
      setShowRoleModal(false);
      setEditingRole(null);
      resetRoleForm();
      toast.success("Role updated successfully");
      // Reload data to ensure consistency
      await loadData();
    } catch (error: any) {
      toast.error(
        "Failed to update role: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm("Are you sure you want to delete this role?")) return;

    try {
      setLoading(true);
      await securityRolesService.deleteRole(roleId);
      setRoles((prev) => prev.filter((role) => role._id !== roleId));
      toast.success("Role deleted successfully");
      // Reload data to ensure consistency
      await loadData();
    } catch (error: any) {
      toast.error(
        "Failed to delete role: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!assignForm.userId || assignForm.roleIds.length === 0) {
      toast.error("Please select a user and at least one role");
      return;
    }

    try {
      await securityRolesService.assignRole({
        userId: assignForm.userId,
        roleIds: assignForm.roleIds,
        branchIds: assignForm.branchIds,
      });
      setShowAssignModal(false);
      resetAssignForm();
      toast.success("Roles assigned successfully");
    } catch (error: any) {
      toast.error(
        "Failed to assign roles: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleUpdateSecurityPolicy = async () => {
    try {
      await securityRolesService.updateSecurityPolicy(policyForm);
      // Don't update local state with form data, reload instead
      setShowPolicyModal(false);
      toast.success("Security policy updated successfully");
      loadData(); // Reload to get updated policy
    } catch (error: any) {
      toast.error(
        "Failed to update security policy: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      name: "",
      displayName: "",
      description: "",
      permissions: [],
      color: "#3B82F6",
      isActive: true,
    });
  };

  const resetAssignForm = () => {
    setAssignForm({
      userId: "",
      roleIds: [],
      branchIds: [],
    });
  };

  const openEditRole = (role: Role) => {
    if (!role) {
      toast.error("Invalid role selected");
      return;
    }

    setEditingRole(role);
    setRoleForm({
      name: role.name || "",
      displayName: role.displayName || "",
      description: role.description || "",
      permissions: Array.isArray(role.permissions)
        ? role.permissions
            .map((p) => (typeof p === "string" ? p : p._id || p.id))
            .filter(Boolean) // Remove any undefined/null values
        : [],
      color: role.color || "#3B82F6",
      isActive: role.isActive ?? true,
    });
    setShowRoleModal(true);
  };

  const togglePermission = (permissionId: string) => {
    setRoleForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((p) => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const toggleRoleAssignment = (roleId: string) => {
    setAssignForm((prev) => ({
      ...prev,
      roleIds: prev.roleIds.includes(roleId)
        ? prev.roleIds.filter((id) => id !== roleId)
        : [...prev.roleIds, roleId],
    }));
  };

  if (loading && !roles.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <span className="text-lg text-gray-600">
              Loading security data...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Security & Roles
            </h1>
            <p className="text-gray-600 mt-1">
              Manage user roles, permissions, and security policies
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {activeTab === "roles" && (
              <button
                onClick={() => {
                  setEditingRole(null);
                  resetRoleForm();
                  setShowRoleModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </button>
            )}
            {activeTab === "assignments" && (
              <button
                onClick={() => {
                  resetAssignForm();
                  setShowAssignModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign Roles
              </button>
            )}
            {activeTab === "security" && (
              <button
                onClick={() => setShowPolicyModal(true)}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Settings className="w-4 h-4 mr-2" />
                Edit Policy
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "roles" && (
          <RolesTab
            roles={roles}
            onEditRole={openEditRole}
            onDeleteRole={handleDeleteRole}
          />
        )}

        {activeTab === "assignments" && (
          <AssignmentsTab users={users} roles={roles} />
        )}

        {activeTab === "security" && (
          <SecurityPolicyTab securityPolicy={securityPolicy} />
        )}

        {activeTab === "audit" && <AuditTab />}
      </div>

      {/* Role Modal */}
      {showRoleModal && (
        <RoleModal
          isOpen={showRoleModal}
          onClose={() => {
            setShowRoleModal(false);
            setEditingRole(null);
            resetRoleForm();
          }}
          role={editingRole}
          roleForm={roleForm}
          setRoleForm={setRoleForm}
          permissions={permissions}
          onSave={editingRole ? handleUpdateRole : handleCreateRole}
          onTogglePermission={togglePermission}
        />
      )}

      {/* Assign Role Modal */}
      {showAssignModal && (
        <AssignRoleModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            resetAssignForm();
          }}
          assignForm={assignForm}
          setAssignForm={setAssignForm}
          users={users}
          roles={roles}
          onSave={handleAssignRole}
          onToggleRole={toggleRoleAssignment}
        />
      )}

      {/* Security Policy Modal */}
      {showPolicyModal && (
        <SecurityPolicyModal
          isOpen={showPolicyModal}
          onClose={() => setShowPolicyModal(false)}
          policyForm={policyForm}
          setPolicyForm={setPolicyForm}
          onSave={handleUpdateSecurityPolicy}
        />
      )}
    </div>
  );
};

// Roles Tab Component
const RolesTab: React.FC<{
  roles: Role[];
  onEditRole: (role: Role) => void;
  onDeleteRole: (roleId: string) => void;
}> = ({ roles, onEditRole, onDeleteRole }) => {
  // Filter out any undefined/null roles and add safety checks
  const validRoles = roles?.filter((role) => role && role._id) || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {validRoles.map((role) => (
          <div
            key={role._id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div
                  className="w-4 h-4 rounded-full mr-3"
                  style={{ backgroundColor: role.color || "#3B82F6" }}
                ></div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {role.displayName || "Unnamed Role"}
                  </h3>
                  <p className="text-sm text-gray-500">{role.name || ""}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => onEditRole(role)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <Edit className="w-4 h-4" />
                </button>
                {!role.isSystemRole && (
                  <button
                    onClick={() => onDeleteRole(role._id!)}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {role.description || "No description provided"}
            </p>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Permissions</span>
                <span className="text-gray-900 font-medium">
                  {role.permissions?.length || 0}
                </span>
              </div>

              {role.isSystemRole && (
                <div className="flex items-center text-xs text-amber-600">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  System Role
                </div>
              )}

              {!role.isActive && (
                <div className="flex items-center text-xs text-red-600">
                  <X className="w-3 h-3 mr-1" />
                  Inactive
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {validRoles.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No roles found</p>
          <p className="text-gray-400">Create your first role to get started</p>
        </div>
      )}
    </div>
  );
};

// Assignments Tab Component
const AssignmentsTab: React.FC<{
  users: any[];
  roles: Role[];
}> = ({ users, roles }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            User Role Assignments
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage which roles are assigned to each user
          </p>
          <div className="mt-2 text-xs text-gray-500">
            {users.length} users • {roles.length} roles available
          </div>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No user assignments found</p>
            <p className="text-gray-400 text-sm">
              Assign roles to users to see them here
            </p>
            {users.length === 0 && (
              <p className="text-amber-500 text-sm mt-2">
                No users loaded. Check user management system.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Security Policy Tab Component
const SecurityPolicyTab: React.FC<{
  securityPolicy: SecurityPolicy | null;
}> = ({ securityPolicy }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Security Policies
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure system-wide security settings
          </p>
        </div>
        <div className="p-6">
          {securityPolicy ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Password Policy</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Minimum Length:</span>
                    <span>{securityPolicy.passwordPolicy?.minLength || 8}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Password Expiry:</span>
                    <span>
                      {securityPolicy.passwordPolicy?.maxAge || "Never"} days
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Session Policy</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Max Session:</span>
                    <span>
                      {securityPolicy.sessionPolicy?.maxSessionDuration || 30}{" "}
                      min
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Idle Timeout:</span>
                    <span>
                      {securityPolicy.sessionPolicy?.idleTimeout || 15} min
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No security policy configured</p>
              <p className="text-gray-400 text-sm">
                Click "Edit Policy" to configure security settings
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Audit Tab Component
const AuditTab: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Access Control Audit
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Monitor role assignments and permission changes
          </p>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Audit logs coming soon</p>
            <p className="text-gray-400 text-sm">
              Track role and permission changes here
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Modal Component
const RoleModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  role: Role | null;
  roleForm: any;
  setRoleForm: (form: any) => void;
  permissions: Permission[];
  onSave: () => void;
  onTogglePermission: (permissionId: string) => void;
}> = ({
  isOpen,
  onClose,
  role,
  roleForm,
  setRoleForm,
  permissions,
  onSave,
  onTogglePermission,
}) => {
  if (!isOpen) return null;

  const groupedPermissions = permissions.reduce((acc, permission) => {
    const category = permission.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {role ? "Edit Role" : "Create Role"}
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Role Name"
                type="text"
                value={roleForm.displayName}
                onChange={(e) =>
                  setRoleForm({ ...roleForm, displayName: e.target.value })
                }
                placeholder="e.g., Branch Manager"
              />
            </div>

            <div>
              <Input
                label="System Name"
                type="text"
                value={roleForm.name}
                onChange={(e) =>
                  setRoleForm({ ...roleForm, name: e.target.value })
                }
                placeholder="e.g., branch_manager"
                helper="Used internally by the system (no spaces, lowercase)"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={roleForm.description}
              onChange={(e) =>
                setRoleForm({ ...roleForm, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe this role's purpose and responsibilities..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Color"
                type="color"
                value={roleForm.color}
                onChange={(e) =>
                  setRoleForm({ ...roleForm, color: e.target.value })
                }
                className="h-10"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={roleForm.isActive}
                onChange={(e) =>
                  setRoleForm({ ...roleForm, isActive: e.target.checked })
                }
                className="mr-2"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700"
              >
                Active Role
              </label>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Permissions
            </h3>
            <div className="space-y-4">
              {Object.entries(groupedPermissions).map(
                ([category, categoryPermissions]) => (
                  <div
                    key={category}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 mb-3">
                      {category}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryPermissions.map((permission) => (
                        <div
                          key={permission._id || permission.id}
                          className="flex items-center"
                        >
                          <input
                            type="checkbox"
                            id={permission._id || permission.id}
                            checked={roleForm.permissions.includes(
                              permission._id || permission.id
                            )}
                            onChange={() =>
                              onTogglePermission(
                                permission._id || permission.id
                              )
                            }
                            className="mr-2"
                          />
                          <label
                            htmlFor={permission._id || permission.id}
                            className="text-sm text-gray-700 cursor-pointer"
                          >
                            {permission.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md"
          >
            <Save className="w-4 h-4 mr-2 inline" />
            {role ? "Update Role" : "Create Role"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Assign Role Modal Component
const AssignRoleModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  assignForm: any;
  setAssignForm: (form: any) => void;
  users: any[];
  roles: Role[];
  onSave: () => void;
  onToggleRole: (roleId: string) => void;
}> = ({
  isOpen,
  onClose,
  assignForm,
  setAssignForm,
  users,
  roles,
  onSave,
  onToggleRole,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Assign Roles</h2>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User
            </label>
            <select
              value={assignForm.userId}
              onChange={(e) =>
                setAssignForm({ ...assignForm, userId: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a user...</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Roles
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
              {roles
                .filter((role) => role.isActive)
                .map((role) => (
                  <div key={role._id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={role._id}
                      checked={assignForm.roleIds.includes(role._id!)}
                      onChange={() => onToggleRole(role._id!)}
                      className="mr-2"
                    />
                    <label
                      htmlFor={role._id}
                      className="flex items-center cursor-pointer"
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: role.color }}
                      ></div>
                      <span className="text-sm text-gray-700">
                        {role.displayName}
                      </span>
                    </label>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded-md"
          >
            <UserPlus className="w-4 h-4 mr-2 inline" />
            Assign Roles
          </button>
        </div>
      </div>
    </div>
  );
};

// Security Policy Modal Component
const SecurityPolicyModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  policyForm: any;
  setPolicyForm: (form: any) => void;
  onSave: () => void;
}> = ({ isOpen, onClose, policyForm, setPolicyForm, onSave }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Security Policy Settings
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                value={policyForm.sessionTimeoutMinutes || ""}
                placeholder="30"
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    sessionTimeoutMinutes:
                      e.target.value === ""
                        ? ""
                        : parseInt(e.target.value) || 30,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="5"
                max="480"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Login Attempts
              </label>
              <input
                type="number"
                value={policyForm.maxLoginAttempts || ""}
                placeholder="5"
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    maxLoginAttempts:
                      e.target.value === ""
                        ? ""
                        : parseInt(e.target.value) || 5,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="3"
                max="10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requirePasswordChange"
                checked={policyForm.requirePasswordChange}
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    requirePasswordChange: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <label
                htmlFor="requirePasswordChange"
                className="text-sm font-medium text-gray-700"
              >
                Require Password Change
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password Change Interval (days)
              </label>
              <input
                type="number"
                value={policyForm.passwordChangeIntervalDays || ""}
                placeholder="90"
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    passwordChangeIntervalDays:
                      e.target.value === ""
                        ? ""
                        : parseInt(e.target.value) || 90,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="30"
                max="365"
                disabled={!policyForm.requirePasswordChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowMultipleSessions"
                checked={policyForm.allowMultipleSessions}
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    allowMultipleSessions: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <label
                htmlFor="allowMultipleSessions"
                className="text-sm font-medium text-gray-700"
              >
                Allow Multiple Sessions
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audit Log Retention (days)
              </label>
              <input
                type="number"
                value={policyForm.auditLogRetentionDays || ""}
                placeholder="365"
                onChange={(e) =>
                  setPolicyForm({
                    ...policyForm,
                    auditLogRetentionDays:
                      e.target.value === ""
                        ? ""
                        : parseInt(e.target.value) || 365,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="30"
                max="2555"
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-purple-600 text-white hover:bg-purple-700 rounded-md"
          >
            <Save className="w-4 h-4 mr-2 inline" />
            Update Policy
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityRolesPage;
